import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowLeft,
    Copy,
    Download,
    RefreshCcw,
    Loader2,
    PenLine,
    CheckCircle2,
    Trash2,
    Edit3,
    Save,
    X as XIcon,
    Sparkles,
    History,
} from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";
import { WordSwap } from "@/components/WordSwap";

const DURATION_LABEL = {
    "60s": "60 seconds",
    "90s": "90 seconds",
    "2m30": "2 minutes 30 seconds",
    "3m": "3 minutes",
};

const SECTION_META = [
    { key: "hook", label: "Hook", note: "First 3 to 5 seconds. Pattern interrupt." },
    { key: "authority_core", label: "Message", note: "The full spoken body. Weave any proof naturally into the flow." },
    { key: "cta", label: "CTA", note: "The action you want them to take" },
];

const STRATEGIST_META = [
    { key: "buyer_psychology", label: "Buyer Psychology" },
    { key: "delivery_note", label: "Delivery Note" },
];

// Parse the AI's timing string into a per-section map, e.g.:
//   "0:00-0:05 Hook. 0:05-0:45 Authority Core. 0:45-0:55 Proof. 0:55-1:00 CTA."
// → { hook: "0:00–0:05", authority_core: "0:05–0:45", proof: "0:45–0:55", cta: "0:55–1:00" }
const parseTiming = (raw) => {
    if (!raw) return {};
    const out = {};
    const labels = {
        hook: /hook/i,
        authority_core: /message|authority[\s_-]?core/i,
        cta: /\bcta\b|call[\s-]?to[\s-]?action/i,
    };
    // Match "M:SS-M:SS Label" or "M:SS - M:SS Label"
    const regex = /(\d{1,2}:\d{2})\s*[-–—]\s*(\d{1,2}:\d{2})\s*([A-Za-z][A-Za-z\s_-]*?)(?=[.,\n]|$|\d)/g;
    let m;
    while ((m = regex.exec(raw)) !== null) {
        const range = `${m[1]}–${m[2]}`;
        const label = m[3].trim();
        for (const [key, pattern] of Object.entries(labels)) {
            if (pattern.test(label) && !out[key]) {
                out[key] = range;
                break;
            }
        }
    }
    return out;
};

const TONE_OPTIONS = [
    "Keep current tone",
    "Bold and direct",
    "Warm and conversational",
    "Educational and clear",
    "Story-driven and emotional",
    "Urgent and punchy",
    "Playful and witty",
    "Custom tone…",
];

const CUSTOM_TONE_SENTINEL = "Custom tone…";

const ScriptView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const confirm = useConfirm();
    const [script, setScript] = useState(null);
    const [batchMates, setBatchMates] = useState([]);
    const [allBatches, setAllBatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [regenerating, setRegenerating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [sectionRegening, setSectionRegening] = useState(null); // section key being regenerated
    const [editingKey, setEditingKey] = useState(null); // "title" | section key
    const [editValue, setEditValue] = useState("");
    const [savingEdit, setSavingEdit] = useState(false);
    const [selectedTone, setSelectedTone] = useState(TONE_OPTIONS[0]);
    const [customTone, setCustomTone] = useState("");
    const [batchesPanelOpen, setBatchesPanelOpen] = useState(false);

    const loadScript = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/scripts/${id}`);
            setScript(data);
            const { data: all } = await api.get(`/projects/${data.project_id}/scripts`);
            // Current batch mates (same generation)
            const mates = all
                .filter((s) => s.batch_id === data.batch_id)
                .sort((a, b) => (a.variant || "").localeCompare(b.variant || ""));
            setBatchMates(mates.length ? mates : [data]);
            // Group all scripts by batch for the previous-generations panel
            const grouped = {};
            all.forEach((s) => {
                const k = s.batch_id || s.id;
                if (!grouped[k]) grouped[k] = [];
                grouped[k].push(s);
            });
            const batches = Object.values(grouped).map((arr) => {
                arr.sort((a, b) => (a.variant || "").localeCompare(b.variant || ""));
                return {
                    batch_id: arr[0].batch_id || arr[0].id,
                    created_at: arr[0].created_at,
                    tone: arr[0].tone,
                    variants: arr,
                };
            });
            batches.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
            setAllBatches(batches);
        } catch {
            toast.error("Script not found");
            navigate("/dashboard");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadScript();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const copyScript = async () => {
        try {
            const hasSections = script.hook || script.authority_core || script.cta;
            const text = hasSections
                ? [
                      script.title,
                      "",
                      script.hook && `HOOK\n${script.hook}`,
                      script.authority_core && `MESSAGE\n${script.authority_core}`,
                      script.cta && `CTA\n${script.cta}`,
                  ]
                      .filter(Boolean)
                      .join("\n\n")
                : `${script.title}\n\n${script.body}`;
            await navigator.clipboard.writeText(text);
            toast.success("Script copied to clipboard");
        } catch {
            toast.error("Copy failed");
        }
    };

    const downloadFile = async (kind) => {
        try {
            const token = localStorage.getItem("uncloned_token");
            const res = await fetch(`${API}/scripts/${id}/${kind}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error();
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const variantSuffix = script.variant ? `-${script.variant}` : "";
            a.download = `${script.title || "script"}${variantSuffix}.${kind}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`${kind.toUpperCase()} downloaded`);
        } catch {
            toast.error(`${kind.toUpperCase()} download failed`);
        }
    };

    const regenerate = async (toneOverride = null) => {
        // Resolve custom tone if user selected the sentinel
        let effectiveTone = toneOverride;
        if (toneOverride === CUSTOM_TONE_SENTINEL) {
            if (!customTone.trim()) {
                toast.error("Please describe your custom tone in the box first");
                return;
            }
            effectiveTone = customTone.trim();
        }
        const tone = effectiveTone === TONE_OPTIONS[0] ? null : effectiveTone;
        const label = tone ? ` with "${tone}"` : "";
        const ok = await confirm({
            title: `Generate 3 New Variants${label}`,
            message: `A new set of 3 variants will be added to this project. Your current generation stays saved.`,
            confirmLabel: "Generate",
            cancelLabel: "Cancel",
            variant: "primary",
        });
        if (!ok) return;
        setRegenerating(true);
        try {
            const payload = { project_id: script.project_id };
            if (tone) payload.tone = tone;
            const { data } = await api.post("/scripts/generate", payload);
            const first = data.scripts?.[0];
            if (first) {
                toast.success(`3 new variants added${label}`);
                navigate(`/scripts/${first.id}`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Regenerate failed");
        } finally {
            setRegenerating(false);
        }
    };

    const regenerateSection = async (sectionKey) => {
        setSectionRegening(sectionKey);
        try {
            const { data } = await api.post(`/scripts/${id}/regenerate-section`, {
                section: sectionKey,
            });
            setScript(data);
            toast.success(`${SECTION_META.find((s) => s.key === sectionKey)?.label} regenerated`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Section regeneration failed");
        } finally {
            setSectionRegening(null);
        }
    };

    const startEdit = (key) => {
        setEditingKey(key);
        setEditValue(script[key] || "");
    };

    const cancelEdit = () => {
        setEditingKey(null);
        setEditValue("");
    };

    const saveEdit = async () => {
        if (!editingKey) return;
        setSavingEdit(true);
        try {
            const payload = { [editingKey]: editValue };
            const { data } = await api.patch(`/scripts/${id}`, payload);
            setScript(data);
            toast.success("Saved");
            setEditingKey(null);
            setEditValue("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSavingEdit(false);
        }
    };

    const inlineReplace = async (sectionKey, newText) => {
        try {
            const { data } = await api.patch(`/scripts/${id}`, { [sectionKey]: newText });
            setScript(data);
        } catch (err) {
            toast.error("Save failed");
        }
    };

    const deleteBatch = async () => {
        const count = batchMates.length;
        const ok = await confirm({
            title: "Delete Generation",
            message: `Delete all ${count} variant${count === 1 ? "" : "s"} in this generation? This can't be undone.`,
            confirmLabel: count === 1 ? "Delete Script" : `Delete ${count} Variants`,
            cancelLabel: "Keep Them",
            variant: "danger",
        });
        if (!ok) return;
        setDeleting(true);
        try {
            await Promise.all(batchMates.map((s) => api.delete(`/scripts/${s.id}`)));
            toast.success("Deleted");
            navigate(`/projects/${script.project_id}`);
        } catch {
            toast.error("Delete failed");
        } finally {
            setDeleting(false);
        }
    };

    const hasSections = useMemo(
        () => script && (script.hook || script.authority_core || script.cta),
        [script],
    );

    const timingMap = useMemo(() => parseTiming(script?.timing), [script?.timing]);

    if (loading || !script) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="script-loading">
                <Loader2 className="w-6 h-6 animate-spin text-un-berry" />
            </div>
        );
    }

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="script-view">
            {/* Top nav */}
            <div className="mb-8">
                <div className="mb-4">
                    <button
                        onClick={() => navigate(`/projects/${script.project_id}`)}
                        className="un-btn-ghost flex items-center gap-2 text-sm"
                        data-testid="script-back"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to project
                    </button>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 mb-5">
                    <Link to={`/projects/${script.project_id}/intake`} className="un-btn-outline text-sm flex items-center gap-2" data-testid="script-edit-intake">
                        <PenLine className="w-4 h-4" /> Edit intake
                    </Link>
                    <button onClick={copyScript} className="un-btn-outline text-sm flex items-center gap-2" data-testid="script-copy">
                        <Copy className="w-4 h-4" /> Copy
                    </button>
                    <button onClick={() => downloadFile("pdf")} className="un-btn-outline text-sm flex items-center gap-2" data-testid="script-download-pdf">
                        <Download className="w-4 h-4" /> PDF
                    </button>
                    <button onClick={() => downloadFile("docx")} className="un-btn-outline text-sm flex items-center gap-2" data-testid="script-download-docx">
                        <Download className="w-4 h-4" /> DOCX
                    </button>
                </div>
                <div className="flex justify-center">
                    {/* Regenerate with visible tone selector */}
                    <div className="flex flex-wrap items-center gap-2 border-2 border-un-onyx bg-un-lime/40 p-2 pr-3">
                        <label className="flex items-center gap-2 pl-1 flex-shrink-0">
                            <span className="un-mono text-[0.7rem] font-bold">TONE:</span>
                            <select
                                value={selectedTone}
                                onChange={(e) => setSelectedTone(e.target.value)}
                                className="un-input !py-1 !px-2 text-sm bg-white min-w-[220px]"
                                data-testid="script-tone-select"
                                disabled={regenerating}
                            >
                                {TONE_OPTIONS.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </label>
                        {selectedTone === CUSTOM_TONE_SENTINEL && (
                            <input
                                type="text"
                                value={customTone}
                                onChange={(e) => setCustomTone(e.target.value)}
                                placeholder="Describe your tone (e.g. UnCloned® direct, luxe, no fluff)"
                                className="un-input !py-1 !px-2 text-sm bg-white min-w-[260px] flex-1"
                                data-testid="script-custom-tone-input"
                                disabled={regenerating}
                            />
                        )}
                        <button
                            onClick={() => regenerate(selectedTone)}
                            disabled={regenerating || (selectedTone === CUSTOM_TONE_SENTINEL && !customTone.trim())}
                            className="un-btn-primary text-sm flex items-center gap-2 flex-shrink-0"
                            data-testid="script-regenerate"
                        >
                            {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                            {regenerating ? "Generating…" : "Generate 3 New Variants"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Variant switcher */}
            {batchMates.length > 1 && (
                <div className="mb-6" data-testid="variant-switcher">
                    <div className="un-label text-un-grape mb-3">Variants in this generation</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {batchMates.map((m) => {
                            const isCurrent = m.id === script.id;
                            return (
                                <Link
                                    key={m.id}
                                    to={`/scripts/${m.id}`}
                                    className={`border-2 border-un-onyx p-4 transition-colors ${
                                        isCurrent ? "bg-un-berry text-white" : "bg-white text-un-onyx hover:bg-un-onyx hover:text-white"
                                    }`}
                                    data-testid={`variant-tab-${m.variant || m.id}`}
                                >
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <span className="un-mono text-xs opacity-80">VARIANT {m.variant || "•"}</span>
                                        <span className="un-mono text-xs opacity-80">{m.word_count}w</span>
                                    </div>
                                    <div className="font-bold text-sm line-clamp-2 leading-snug">{m.title}</div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Previous Generations */}
            {allBatches.length > 1 && (
                <div className="mb-8">
                    <button
                        onClick={() => setBatchesPanelOpen((v) => !v)}
                        className="un-btn-ghost text-xs inline-flex items-center gap-2"
                        data-testid="prev-generations-toggle"
                    >
                        <History className="w-4 h-4" />
                        {batchesPanelOpen ? "Hide" : "Show"} All {allBatches.length} Generations
                    </button>
                    {batchesPanelOpen && (
                        <div className="mt-3 border-2 border-un-onyx bg-white" data-testid="prev-generations-panel">
                            {allBatches.map((batch, batchIdx) => {
                                const isCurrentBatch = batch.batch_id === script.batch_id;
                                return (
                                    <div key={batch.batch_id} className="border-b-2 border-un-onyx last:border-b-0">
                                        <div className={`px-4 py-3 flex items-center justify-between ${isCurrentBatch ? "bg-un-berry text-white" : "bg-un-onyx/5"}`}>
                                            <div className="flex items-center gap-3">
                                                <span className="un-mono text-xs font-bold">
                                                    GENERATION {allBatches.length - batchIdx}
                                                </span>
                                                {batch.tone && (
                                                    <span className="un-mono text-xs opacity-80">· {batch.tone}</span>
                                                )}
                                                {isCurrentBatch && <span className="un-mono text-xs">· current</span>}
                                            </div>
                                            <span className="un-mono text-xs opacity-70">
                                                {new Date(batch.created_at).toLocaleString()}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                                            {batch.variants.map((v) => (
                                                <Link
                                                    key={v.id}
                                                    to={`/scripts/${v.id}`}
                                                    className={`p-3 border-r-2 border-t-2 border-un-onyx/20 hover:bg-un-berry hover:text-white ${
                                                        v.id === script.id ? "bg-un-onyx text-white" : ""
                                                    }`}
                                                    data-testid={`prev-variant-${v.id}`}
                                                >
                                                    <div className="un-mono text-[0.65rem] opacity-70 mb-1">VARIANT {v.variant || "•"} · {v.word_count}w</div>
                                                    <div className="text-sm font-bold line-clamp-2">{v.title}</div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Meta sidebar */}
                <div className="lg:col-span-3">
                    <div className="un-card p-6 lg:sticky lg:top-24" data-testid="script-meta">
                        {script.variant && (
                            <div className="un-pill un-pill-berry mb-4 inline-block">Variant {script.variant}</div>
                        )}
                        <div className="un-label text-un-grape mb-2">Version</div>
                        <div className="un-h1 text-5xl mb-6">V{script.version}</div>
                        <div className="space-y-4 text-sm">
                            <div>
                                <div className="un-label text-un-grape text-[0.6rem]">Duration</div>
                                <div className="mt-1">{DURATION_LABEL[script.duration] || script.duration}</div>
                            </div>
                            <div>
                                <div className="un-label text-un-grape text-[0.6rem]">Tone</div>
                                <div className="mt-1">{script.tone || "—"}</div>
                            </div>
                            <div>
                                <div className="un-label text-un-grape text-[0.6rem]">Word count</div>
                                <div className="mt-1 un-mono">{script.word_count}</div>
                            </div>
                            <div>
                                <div className="un-label text-un-grape text-[0.6rem]">Generated</div>
                                <div className="mt-1 un-mono text-xs">
                                    {new Date(script.created_at).toLocaleString()}
                                </div>
                            </div>
                            {script.edited && (
                                <div className="un-mono text-xs text-un-berry font-bold">EDITED</div>
                            )}
                        </div>
                        <div className="un-divider mt-6 pt-5">
                            <div className="flex items-center gap-2 text-sm text-un-onyx/70" data-testid="autosave-note">
                                <CheckCircle2 className="w-4 h-4 text-un-berry flex-shrink-0" />
                                <span>Auto-saved. Every generation is kept as its own version.</span>
                            </div>
                        </div>
                        <button
                            onClick={deleteBatch}
                            disabled={deleting}
                            className="un-btn-ghost text-xs mt-4 !px-0 text-un-onyx/40 hover:!text-un-berry flex items-center gap-1"
                            data-testid="script-delete-batch"
                        >
                            <Trash2 className="w-3 h-3" />
                            {batchMates.length > 1 ? `Delete all ${batchMates.length} variants` : "Delete this script"}
                        </button>
                    </div>
                </div>

                {/* Script content */}
                <div className="lg:col-span-9">
                    <div className="un-card p-8 md:p-12">
                        <div className="un-label text-un-grape mb-3">Script · Variant {script.variant || "—"}</div>

                        {/* Title (editable) */}
                        {editingKey === "title" ? (
                            <div className="mb-8">
                                <textarea
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    className="un-input text-3xl font-bold w-full"
                                    rows={2}
                                    data-testid="edit-title-input"
                                />
                                <div className="flex gap-2 mt-2">
                                    <button onClick={saveEdit} disabled={savingEdit} className="un-btn-primary text-xs inline-flex items-center gap-1" data-testid="edit-title-save">
                                        {savingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                                    </button>
                                    <button onClick={cancelEdit} className="un-btn-outline text-xs inline-flex items-center gap-1">
                                        <XIcon className="w-3 h-3" /> Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start gap-3 mb-8">
                                <h1 className="un-h1 text-4xl md:text-5xl leading-tight flex-1" data-testid="script-title">
                                    {script.title}
                                </h1>
                                <button
                                    onClick={() => startEdit("title")}
                                    className="un-btn-ghost !px-2 !py-1 text-xs inline-flex items-center gap-1 mt-2"
                                    data-testid="edit-title-btn"
                                    title="Edit title"
                                >
                                    <Edit3 className="w-3 h-3" />
                                </button>
                            </div>
                        )}

                        <div className="un-divider pt-8">
                            {script.idea && (
                                <div className="mb-8 p-4 border-2 border-un-onyx bg-un-lime/40 max-w-3xl" data-testid="script-idea">
                                    <div className="un-label text-un-grape text-xs mb-2">The Idea</div>
                                    <div className="text-un-onyx font-semibold">{script.idea}</div>
                                </div>
                            )}
                            {Array.isArray(script.hook_options) && script.hook_options.length > 0 && (
                                <div className="mb-8 max-w-3xl" data-testid="script-hook-options">
                                    <div className="un-label text-un-grape text-xs mb-3">Alternate Hook Options — Tap To Swap In</div>
                                    <div className="space-y-2">
                                        {script.hook_options.map((h, i) => (
                                            <button
                                                key={i}
                                                onClick={() => inlineReplace("hook", h)}
                                                className="block w-full text-left border-2 border-un-onyx px-4 py-3 hover:bg-un-berry hover:text-white transition-colors bg-white text-un-onyx"
                                                data-testid={`hook-option-${i}`}
                                            >
                                                <span className="un-mono text-[0.65rem] font-bold text-un-grape mr-2">HOOK {i + 1}</span>
                                                {h}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {hasSections ? (
                                <div className="space-y-10 max-w-3xl" data-testid="script-sections">
                                    {SECTION_META.map(({ key, label, note }) => {
                                        const text = script[key];
                                        if (!text && editingKey !== key) return null;
                                        const isEditing = editingKey === key;
                                        const isRegening = sectionRegening === key;
                                        return (
                                            <section key={key} data-testid={`section-${key}`}>
                                                <div className="flex items-center flex-wrap gap-3 mb-4">
                                                    <span className="un-pill un-pill-berry" data-testid={`section-label-${key}`}>{label}</span>
                                                    {timingMap[key] && (
                                                        <span
                                                            className="un-mono text-xs font-bold bg-un-water/60 border-2 border-un-onyx px-2 py-1"
                                                            data-testid={`section-timing-${key}`}
                                                            title="Time budget for this section"
                                                        >
                                                            {timingMap[key]}
                                                        </span>
                                                    )}
                                                    <span className="un-mono text-xs text-un-onyx/50">{note}</span>
                                                    <div className="flex-1" />
                                                    {!isEditing && (
                                                        <>
                                                            <button
                                                                onClick={() => regenerateSection(key)}
                                                                disabled={isRegening || !!sectionRegening}
                                                                className="un-btn-ghost !px-2 !py-1 text-[0.65rem] inline-flex items-center gap-1"
                                                                data-testid={`section-regen-${key}`}
                                                                title={`Regenerate just this ${label}`}
                                                            >
                                                                {isRegening ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                                                {isRegening ? "Regenerating…" : "Regenerate"}
                                                            </button>
                                                            <button
                                                                onClick={() => startEdit(key)}
                                                                className="un-btn-ghost !px-2 !py-1 text-[0.65rem] inline-flex items-center gap-1"
                                                                data-testid={`section-edit-${key}`}
                                                            >
                                                                <Edit3 className="w-3 h-3" /> Edit
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                                {isEditing ? (
                                                    <div>
                                                        <textarea
                                                            value={editValue}
                                                            onChange={(e) => setEditValue(e.target.value)}
                                                            rows={Math.min(20, Math.max(4, (editValue || "").split("\n").length + 2))}
                                                            className="un-input w-full un-script"
                                                            data-testid={`edit-${key}-input`}
                                                        />
                                                        <div className="flex gap-2 mt-2">
                                                            <button onClick={saveEdit} disabled={savingEdit} className="un-btn-primary text-xs inline-flex items-center gap-1" data-testid={`edit-${key}-save`}>
                                                                {savingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                                                            </button>
                                                            <button onClick={cancelEdit} className="un-btn-outline text-xs inline-flex items-center gap-1">
                                                                <XIcon className="w-3 h-3" /> Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <WordSwap
                                                        scriptId={script.id}
                                                        section={key}
                                                        currentText={text || ""}
                                                        onReplace={(newText) => inlineReplace(key, newText)}
                                                    />
                                                )}
                                            </section>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="un-script max-w-3xl" data-testid="script-body">
                                    {script.body}
                                </div>
                            )}
                            {STRATEGIST_META.some((s) => script[s.key]) && (
                                <div className="mt-12 pt-8 un-divider max-w-3xl" data-testid="strategist-panel">
                                    <div className="un-label text-un-grape text-sm mb-4">Strategist Notes</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {STRATEGIST_META.map(({ key, label }) => script[key] ? (
                                            <div key={key} className="border-2 border-un-onyx p-5 bg-white" data-testid={`strategist-${key}`}>
                                                <div className="un-mono text-xs font-bold text-un-berry mb-3">{label.toUpperCase()}</div>
                                                <div className="text-base text-un-onyx whitespace-pre-wrap leading-relaxed">{script[key]}</div>
                                            </div>
                                        ) : null)}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScriptView;
